<template>
  <div>
    <v-hover v-slot="{ isHovering, props }">
      <v-card
        elevation="0"
        :loading="loading"
        tile
        v-bind="props"
        :disabled="loading"
      >
        <!-- Custom loader with color -->
        <template v-slot:loader="{ isActive }">
          <v-progress-linear
            :active="isActive"
            color="primary"
            height="3"
            indeterminate
          ></v-progress-linear>
        </template>

        <!-- Error Alert -->
        <v-alert
          v-if="error"
          type="error"
          class="mb-2"
          density="compact"
          variant="tonal"
        >
          {{ error }}
        </v-alert>

        <!-- Main Content -->
        <div v-else-if="hasData">
          <h2 class="text-h6 text-primary">
            <v-icon left color="secondary">mdi-waves</v-icon>
            Waves
          </h2>
          <compass-direction :direction="latestDataPoint.direction" />
          Direction:
          <b>{{ latestDataPoint.direction.toFixed(0) }}°</b> ({{
            direction2txt(latestDataPoint.direction)
          }})
          <br />
          Sig. wave height:
          <b>{{ latestDataPoint.significantWaveHeight.toFixed(1) }} m</b>
          <br />
          Max. wave height:
          <b>{{ latestDataPoint.maxWaveHeight.toFixed(1) }} m</b>
        </div>

        <!-- No Data Message -->
        <div v-else-if="!loading" class="text-center pa-4">
          <v-icon color="grey" size="large">mdi-waves</v-icon>
          <p class="text-grey mt-2">No wave data available</p>
        </div>

        <!-- Hover Overlay -->

        <v-overlay
          :model-value="isHovering && !error && hasData"
          class="align-center justify-center"
          scrim="rgba(138, 177, 211, 0.4)"
          contained
          @click="show = true"
          style="cursor: pointer"
        >
          <v-btn variant="flat" color="secondary">See more info</v-btn>
        </v-overlay>
      </v-card>
    </v-hover>

    <!-- Overlay with detailed information -->
    <v-overlay
      v-model="show"
      :opacity="0.6"
      color="rgba(138, 177, 211)"
      z-index="7"
      class="d-flex align-center justify-center overlay-primary"
    >
      <v-card light class="chart-container" :max-width="cardMaxWidth">
        <v-container
          fluid
          class="pa-1 pt-0 d-flex flex-column justify-space-between"
        >
          <!-- First Row: Help Button, Timestamp, Close Button -->
          <v-row justify="space-between" align="center" class="top-row">
            <v-col cols="auto">
              <v-btn
                color="primary lighten-1"
                @click="help = !help"
                dark
                fab
                elevation="0"
                small
                class="mt-1"
              >
                <v-icon>mdi-help</v-icon>
              </v-btn>
            </v-col>

            <v-col cols="auto">
              <v-btn
                color="primary"
                @click="show = false"
                dark
                fab
                elevation="0"
                small
                class="mt-1"
              >
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-col>
          </v-row>

          <v-row justify="center" align="center" class="time">
            <v-col cols="auto" class="text-center pa-0">
              <timestamp-status
                :latestTimestamp="latestTimestamp"
              ></timestamp-status>
            </v-col>
          </v-row>

          <!-- Error Alert in Overlay -->
          <v-alert v-if="error" type="error" class="ma-4" variant="tonal">
            {{ error }}
          </v-alert>

          <!-- Second Row: Rose Chart and Wave Height Chart -->
          <v-row
            v-else
            :class="
              display.smAndDown
                ? 'd-flex flex-row'
                : 'd-flex flex-row justify-space-between'
            "
            class="charts-row"
            align="stretch"
          >
            <!-- Rose Chart -->
            <v-col :cols="12" :md="12" :lg="6" :xl="5" class="chart-container">
              <PlotCustom
                v-if="hasData && roseData.length"
                class="plotly-chart"
                :data="roseData"
                :layout="roseLayout"
                :config="{ responsive: true, displayModeBar: false }"
              ></PlotCustom>
              <div v-else class="text-center">No direction data available.</div>
            </v-col>

            <!-- Wave Height Chart -->
            <v-col :cols="12" :md="12" :lg="6" :xl="5" class="chart-container">
              <PlotCustom
                v-if="hasData && data.length"
                class="plotly-chart"
                :data="data"
                :layout="layout"
                :config="{ responsive: true, displayModeBar: false }"
              ></PlotCustom>
              <div v-else class="text-center">
                No wave height data available.
              </div>
            </v-col>
          </v-row>
        </v-container>
      </v-card>
    </v-overlay>



<!-- Help Overlay -->
<v-overlay
  v-model="help"
  :opacity="0.4"
  color="primary"
  z-index="7"
  class="d-flex align-center justify-center help-overlay"
>
  <v-container fluid class="pa-0 ma-0 pt-4">
    <v-row no-gutters>
      <v-col cols="12" md="10" lg="8" class="mx-auto pb-0">
        <v-alert color="secondary" class="pa-4 help-alert">
          <!-- Fixed Header -->
          <div class="help-header">
            <h3 class="text-h6">Wave Measurements & Douglas Sea Scale</h3>
            <v-btn color="white" @click="help = !help" icon="mdi-close" size="small">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>

          <!-- Scrollable Content -->
          <div class="help-content">
            <!-- Wave Height Measurements Section -->
            <div class="measurement-explanation mb-6">
              <h4 class="text-h6 mb-3">Wave Height Measurements</h4>
              <p class="mb-3">
                The <b>significant wave height (Hs)</b> is the mean height of
                the highest one third of the waves passing a point. This wave
                height correlates best with the wave height a trained observer
                reports after examining a group of wave heights over a set
                period from a ship or platform.
              </p>
              <p class="mb-4">
                The likely maximum wave height (Hmax) will be roughly double the
                significant wave height (Hs). The
                <b>maximum wave height (Hmax)</b> observed over a given observed
                period (20 minutes). The <b>wave direction</b> is presented with
                respect to true north, representing where the waves are 'coming
                from'.
              </p>
            </div>

            <!-- Douglas Scale Section -->
            <div class="douglas-scale-section">
              <h4 class="text-h6 mb-3">Douglas Sea Scale</h4>
              <p class="text-body-1 mb-4">
                The Douglas Sea Scale is used by mariners and meteorologists to classify 
                sea conditions based on wave height. Below is the complete scale with 
                corresponding wave heights and sea state descriptions.
              </p>
              
              <div class="douglas-grid">
                <v-card
                  v-for="item in douglasScale"
                  :key="item.degree"
                  class="douglas-card d-flex"
                  elevation="2"
                >
                  <div 
                    class="color-strip"
                    :style="{ backgroundColor: getWaveColor(item.degree) }"
                  ></div>
                  <div class="card-content flex-grow-1">
                    <v-card-title class="text-h6 d-flex justify-space-between align-center">
                      <span class="degree-number">{{ item.degree }}</span>
                      <span class="degree-name">{{ item.name }}</span>
                    </v-card-title>
                    <v-card-text>
                      <div class="d-flex flex-column">
                        <div class="wave-height">
                          <v-icon icon="mdi-waves" class="mr-1"></v-icon>
                          {{ item.height }} m
                        </div>
                        <div class="sea-conditions">
                          <v-icon icon="mdi-information" class="mr-1"></v-icon>
                          {{ item.seaConditions }}
                        </div>
                      </div>
                    </v-card-text>
                  </div>
                </v-card>
              </div>
            </div>
          </div>
        </v-alert>
      </v-col>
    </v-row>
  </v-container>
</v-overlay>
  </div>
</template>

<script>
import PlotCustom from "@/components/PlotCustom.vue";
import { generateRose, waveColorSchemes } from "@/plugins/helper.js";
import timestampStatus from "@/components/TimestampStatus.vue";
import direction2txt from "@/plugins/directionClassification";
import CompassDirection from "@/components/CompassDirection.vue";
import { useDisplay } from "vuetify";
import { fetchWavesData } from "@/Service/waveService";
import generateShapes from "@/plugins/verticalIntervals";

export default {
  name: "WavesOverlay",
  components: {
    PlotCustom,
    timestampStatus,
    CompassDirection,
  },
  props: {
    site: {
      type: String,
      default: "Tasman_Bay 119",
      required: true,
    },
  },
  emits: ["noData", "latestDate"],

  data() {
    return {
      show: false,
      help: false,
      loading: true,
      hasData: false,
      error: null,
      latestDataPoint: {
        significantWaveHeight: 0,
        maxWaveHeight: 0,
        direction: 0,
      },
      data: [],
      roseData: [],
      dtick: 10,
      refresher: null,
      interval: 300000,
      highContrast: true,
      latestTimestamp: null,
      // Description taken from : https://www.noaa.gov/jetstream/ocean/waves/jetstream-max-wind-and-sea-scales
      douglasScale: [
        { degree: 0, name: 'Calm (Glassy)', height: '0', seaConditions: 'Sea like a mirror' },
        { degree: 1, name: 'Calm (Rippled)', height: '0-0.1', seaConditions: 'Ripples with the appearance of scales are formed, but without foam crests.' },
        { degree: 2, name: 'Smooth', height: '0.1-0.5', seaConditions: 'Small wavelets, still short but more pronounced, crests have a glassy appearance and do not break.' },
        { degree: 3, name: 'Slight', height: '0.5-1.25', seaConditions: 'Large wavelets. Crests begin to break. Foam of glassy appearance.' },
        { degree: 4, name: 'Moderate', height: '1.25-2.5', seaConditions: 'Small waves, becoming longer.' },
        { degree: 5, name: 'Rough', height: '2.5-4', seaConditions: '	Moderate waves, taking a more pronounced long form. (Chance of some spray).' },
        { degree: 6, name: 'Very Rough', height: '4-6', seaConditions: 'Large waves forming, white foam crests extensive, some spray' },
        { degree: 7, name: 'High', height: '6-9', seaConditions: 'Sea heaps up, foam begins to be blown in streaks' },
        { degree: 8, name: 'Very High', height: '9-14', seaConditions: 'Moderately high long waves, foam blown in well-marked streaks' },
        { degree: 9, name: 'Phenomenal', height: '>14', seaConditions: 'Very high waves with overhanging crests, sea surface white with foam' }
      ]
    };    
  },

  computed: {
    display() {
      return useDisplay();
    },
    cardMaxWidth() {
      const screenWidth = this.display.width;
      if (screenWidth >= 1920) return "80vw";
      if (screenWidth >= 1280) return "90vw";
      return "95vw";
    },
    roseLayout() {
      const screenWidth = this.display.width;
      const marginSettings = screenWidth >= 1920
        ? { l: 80, r: 80, t: 80, b: 80 }
        : screenWidth >= 960
          ? { l: 50, r: 50, t: 50, b: 50 }
          : { l: 25, r: 25, t: 50, b: 50 };

      return {
        shapes: [],
        annotations: [],
        polar: {
          radialaxis: {
            dtick: this.dtick,
            ticksuffix: "%",
            angle: 45,
          },
          barmode: "stack",
          bargap: 0,
          angularaxis: { direction: "clockwise" },
        },
        autoresize: true,
        autosize: true,
        margin: marginSettings,
        showlegend: true,
        legend: {
          orientation: "h",
          y: -0.3,
          x: 0.5,
          xanchor: "center",
          yanchor: "top",
        },
        title: {
          text: "Wave Direction",
          font: { size: screenWidth >= 1920 ? 16 : 14 },
        },
      };
    },
    layout() {
      const screenWidth = this.display.width;
      let marginSettings = screenWidth >= 1920
        ? { b: 80, l: 80, r: 40, t: 40 }
        : screenWidth >= 960
          ? { b: 60, l: 60, r: 20, t: 20 }
          : { b: 50, l: 50, r: 20, t: 20 };

      // Find the maximum wave height in the data
      let maxHeight = 4; // Default minimum range
      if (this.data.length > 0) {
        const heights = this.data.flatMap(d => d.y);
        maxHeight = Math.max(...heights, maxHeight);
      }

      // Add some padding to the max height and round up
      const paddedMax = Math.ceil(maxHeight * 1.2);

      // Create intervals based on Douglas scale
      const verticalIntervals = [
        {
          label: "Calm (0-0.1m)",
          labelLocation: 0.05,
          upper: 0.1,
          lower: 0,
          color: waveColorSchemes[0],
          opacity: 0.8
        },
        {
          label: "Smooth (0.1-0.5m)",
          labelLocation: 0.3,
          upper: 0.5,
          lower: 0.1,
          color: waveColorSchemes[1],
          opacity: 0.8
        },
        {
          label: "Slight (0.5-1.25m)",
          labelLocation: 0.9,
          upper: 1.25,
          lower: 0.5,
          color: waveColorSchemes[2],
          opacity: 0.8
        }
      ];

      // Only add higher wave states if we have data in those ranges
      if (paddedMax > 1.25) {
        verticalIntervals.push({
          label: "Moderate (1.25-2.5m)",
          labelLocation: 1.9,
          upper: 2.5,
          lower: 1.25,
          color: waveColorSchemes[3],
          opacity: 0.8
        });
      }

      if (paddedMax > 2.5) {
        verticalIntervals.push({
          label: "Rough (2.5-4m)",
          labelLocation: 3.25,
          upper: 4,
          lower: 2.5,
          color: waveColorSchemes[4],
          opacity: 0.8
        });
      }

      if (paddedMax > 4) {
        verticalIntervals.push({
          label: "Very Rough (>4m)",
          labelLocation: 4.5,
          upper: paddedMax,
          lower: 4,
          color: waveColorSchemes[5],
          opacity: 0.8
        });
      }

      let [annotations, shapes] = generateShapes(verticalIntervals, {
        color: "#000000",
        bgcolor: "rgba(255, 255, 255, 0.7)"
      });

      return {
        shapes,
        annotations,
        autoresize: true,
        autosize: true,
        showlegend: false,
        margin: marginSettings,
        yaxis: {
          title: "Wave Height [m]",
          ticks: "inside",
          range: [0, paddedMax],
          showgrid: false,
        },
        xaxis: {
          zeroline: true,
          showgrid: false,
        },
      };
    },
  },

  methods: {
    getWaveColor(degree) {
      if (degree <= 1) return waveColorSchemes[0];      // Calm
      if (degree <= 2) return waveColorSchemes[1];      // Smooth
      if (degree <= 3) return waveColorSchemes[2];      // Slight
      if (degree <= 4) return waveColorSchemes[3];      // Moderate
      if (degree <= 5) return waveColorSchemes[4];      // Rough
      return waveColorSchemes[5];                       // Very rough and above
    },
    direction2txt,

    async refreshData() {
      this.loading = true;
      this.error = null;

      try {
        const response = await fetchWavesData(this.site, 0);

        if (!response.success) {
          this.error = response.error;
          this.handleNoData();
          return;
        }

        if (response.noDataAvailable) {
          this.handleNoData();
          return;
        }

        const sortedData = response.time
          .map((time, index) => ({
            time: new Date(time),
            significantWaveHeight: response.significantHeight[index] || 0,
            maxWaveHeight: response.maxHeight[index] || 0,
            direction: response.direction[index] || 0,
          }))
          .sort((a, b) => a.time - b.time)
          .filter(
            (d) =>
              !isNaN(d.significantWaveHeight) &&
              !isNaN(d.maxWaveHeight) &&
              !isNaN(d.direction)
          );

        if (sortedData.length === 0) {
          this.handleNoData();
          return;
        }

        this.hasData = true;
        this.$emit("noData", { type: "waves", hasData: true });

        const latestData = sortedData[sortedData.length - 1];
        this.latestDataPoint = {
          significantWaveHeight: latestData.significantWaveHeight,
          maxWaveHeight: latestData.maxWaveHeight,
          direction: latestData.direction,
        };

        this.latestTimestamp = latestData.time;
        this.$emit("latestDate", this.latestTimestamp);

        // Create plot data with black lines
        const sigHeight = {
          x: sortedData.map((d) => d.time),
          y: sortedData.map((d) => d.significantWaveHeight),
          type: "scatter",
          mode: "lines",
          name: "Significant wave height",
          hovertemplate:
            "Significant wave height: %{y:.2f}m<br>%{x}<extra></extra>",
          line: {
            color: '#fff',
            width: 2,
          },
        };

        const maxHeight = {
          x: sortedData.map((d) => d.time),
          y: sortedData.map((d) => d.maxWaveHeight),
          type: "scatter",
          mode: "lines",
          name: "Maximum wave height",
          hovertemplate:
            "Maximum wave height: %{y:.2f}m<br>%{x}<extra></extra>",
          line: {
            color: '#fffe',
            dash: "dot",
            width: 1,
          },
        };

        const directions = sortedData.map((d) => d.direction);
        if (directions.some((d) => !isNaN(d))) {
          const rose = generateRose(
            {
              direction: directions,
              magnitude: sortedData.map((d) => d.significantWaveHeight),
            },
            22.5,
            "Metres"
          );

          this.roseData = rose[0];
          this.dtick = rose[1];
        } else {
          this.roseData = [];
        }

        this.data = [sigHeight, maxHeight];
      } catch (error) {
        this.error = "Unable to fetch wave data";
        this.handleNoData();
      } finally {
        this.loading = false;
        if (!this._isDestroyed) {
          clearTimeout(this.refresher);
          this.refresher = setTimeout(this.refreshData, this.interval);
        }
      }
    },

    handleNoData() {
      this.clearData();
      this.$emit("noData", { type: "waves", hasData: false });
    },

    clearData() {
      this.hasData = false;
      this.latestDataPoint = {
        significantWaveHeight: null,
        maxWaveHeight: null,
        direction: null,
      };
      this.roseData = [];
      this.data = [];
      this.latestTimestamp = null;
    },
  },

  watch: {
    site: {
      immediate: true,
      handler() {
        clearTimeout(this.refresher);
        this.clearData();
        this.refreshData();
      },
    },
  },

  created() {
    this.refreshData();
  },

  beforeUnmount() {
    clearTimeout(this.refresher);
    this.$emit("noData", { type: "waves", hasData: true });
  },
};
</script>

<style scoped>

.chart-container {
  position: relative;
  width: 100%;
  min-width: 100vw;
  max-height: 100vh;
  overflow-y: auto;
  min-height: 320px;
}

.help-alert {
  max-height: 90vh !important; /* Override Vuetify's styles */
  overflow-y: auto !important; /* Override Vuetify's styles */
  padding: 1rem;
  margin: 0;
}

.help-header {

  top: 0;
  background: inherit;
  z-index: 2;
  padding-bottom: 1rem;
  margin-bottom: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.help-content {
  flex: 1;

  padding-right: 8px;
}

.measurement-explanation {
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding-bottom: 1rem;
}

.douglas-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: .5rem;
  margin-top: 1rem;
  padding-bottom: 2rem;
}

.douglas-card {
  border-radius: 8px;
  transition: transform 0.2s;
  overflow: hidden;
  background: white;
  border: .5px solid rgba(251, 251, 251, 0.344);
}

.douglas-card:hover {
  transform: translateY(-2px);
}

.card-content {
  padding: 8px;
}

.color-strip {
  width: 16px;
  min-height: 100%;
}

.degree-number {
  font-size: 1.5rem;
  font-weight: bold;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.degree-name {
  font-size: 1.1rem;
  flex-grow: 1;
  text-align: right;
}

.wave-height {
  font-size: 1rem;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
}

.sea-conditions {
  font-size: 0.9rem;
  color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: flex-start;
}

.sea-conditions .v-icon {
  margin-top: 4px;
}

/* Responsive styles */
@media (max-width: 600px) {
  .help-content {
    padding-right: 4px;
  }

  .douglas-grid {
    grid-template-columns: 1fr;
    gap: 0.45rem;
  }
  
  .douglas-card {
    margin-bottom: 0.5rem;
  }

  .degree-name {
    font-size: 1rem;
  }

  .wave-height, .sea-conditions {
    font-size: 0.9rem;
  }

  .measurement-explanation {
    font-size: 0.95rem;
  }
}

@media (min-width: 960px) and (max-width: 1919px) {
  .chart-container {
    min-width: 600px;
    min-height: 500px;
    overflow-y: auto;
  }
}

@media (min-width: 1920px) {
  .chart-container {
    min-width: 1200px;
    min-height: 1000px;
  }
}

.plotly-chart {
  position: relative;
  width: 100%;
  height: 100%;
}

.charts-row {
  flex-grow: 1;
}

.top-p {
  margin-bottom: 1rem;
}
</style>

